/* ───── Zone Manifest Types ─────
 *
 * Mirrors the shape of assets/manifests/zones.json.
 * Read once at boot; individual zone configs are passed into
 * MapBuilder and ZoneScene.
 */

export interface ZoneTransition {
  kind: "ferry" | "bridge" | "portal";
  targetZone: string;
  targetSpawn: [number, number];
  dockPosition?: [number, number];
  bridgePosition?: [number, number];
  approachRadius: number;
  label: string;
}

export interface ZonePropsConfig {
  signposts: Array<[number, number]>;
  flowers: {
    red: Array<[number, number]>;
    yellow: Array<[number, number]>;
    blue: Array<[number, number]>;
  };
  bushes: Array<[number, number]>;
  trailStones: Array<[number, number]>;
  wells: Array<[number, number]>;
}

export interface ZoneConfig {
  /** Optional — sidescroll zones have no tilemap and may omit this. */
  type?: "procedural" | "tiled";
  /**
   * Render perspective. Defaults to "iso" if absent so existing zones keep working.
   * Side-scroll lives in its own scene and is routed at boot, not handled here.
   */
  projection?: "iso" | "topdown" | "oblique" | "sidescroll";
  gridSize: number;
  seed?: number;
  waterThreshold?: number;
  spawnPoint: [number, number];
  trees?: Array<[number, number]>;
  props?: ZonePropsConfig;
  solidProps?: Array<[number, number]>;
  transitions: ZoneTransition[];
  /* Tiled-only fields (Task 3) */
  tmj?: string;
  tilesets?: string[];
  collision?: string;
  /**
   * Pre-registered Phaser texture key for the zone's tileset. When set, BootScene
   * skips URL-based tileset loading and the renderer uses this key directly.
   * Useful for procedurally-generated tilesets (e.g. top-down placeholder).
   */
  tilesetKey?: string;
}

export type ZoneManifest = Record<string, ZoneConfig>;

/**
 * Read the zone manifest from Phaser's JSON cache.
 * Call after BootScene has loaded "zone-manifest".
 */
export function getZoneManifest(scene: Phaser.Scene): ZoneManifest {
  return scene.cache.json.get("zone-manifest") as ZoneManifest;
}

/** Convenience: get a single zone config or throw. */
export function getZoneConfig(scene: Phaser.Scene, zoneId: string): ZoneConfig {
  const manifest = getZoneManifest(scene);
  const cfg = manifest[zoneId];
  if (!cfg) throw new Error(`Unknown zone: ${zoneId}`);
  return cfg;
}
